import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Save, Undo2, Loader2, X, ArrowLeft, TriangleAlert, Tag, Percent } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { revenuePostingService } from '../../services/revenuePostingService';
import { isAdmin } from '../../utils/permissions';
// The discount catalogue is defined in the FRONT END, and md_discount_config
// holds only the deviations from it. Reading the table alone returns nothing on
// a fresh tenant, which is exactly how the type picker came up empty.
import {
  DISCOUNT_GROUPS, ALL_DISCOUNT_TYPES, DISCOUNT_BY_CODE, isEnabled,
} from '../../config/discountTypes';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { useColumnFilters, FilterPopover } from '../../components/gridFilters';
import PickerPopover from '../../components/PickerPopover';

// =============================================================================
// Revenue › Budget Setup › Revenue Posting
//
// The catalogue that turned the consolidated ledger from a cost list into a
// P&L. Neither bud_selling_prices nor bud_sales_volumes has ever carried a GL
// account, so revenue could not be consolidated at all — no margin, no debtors
// on the cash flow, no profit to close into retained earnings.
//
// TWO CATALOGUES, TWO TABS:
//
//   GROSS REVENUE     price x volume. Scoped site -> customer group ->
//                     customer, most specific wins. START WITH ONE ROW that
//                     scopes to nothing: that is "all customers", and it is the
//                     rule that stops revenue posting nowhere.
//
//   DISCOUNTS         the same, plus an optional discount TYPE.
//
// Discounts were briefly split across two tabs — untyped rules on one, typed on
// the other. That was wrong and it is worth saying why, because the temptation
// comes back: the two tabs read and wrote the SAME TABLE and differed only by a
// filter on discount_code. Nothing structural, purely presentational, and it
// cost the one thing this page exists to make legible — you could not see the
// resolution order, because a typed rule and the customer rule it outranks were
// never on screen together.
//
// Type is simply the third optional scope column, and it behaves exactly like
// the other two: Site reads "All sites" when empty, Applies To reads "All
// customers", Discount Type reads "All types". Rows sort broadest-first, so the
// list reads down in the order the LATERAL resolves.
//
// THE RULING WORTH KNOWING: on discounts, TYPE OUTRANKS CUSTOMER. A discount's
// account follows what kind of spend it is — settlement is financing, a promo
// is marketing, a listing fee is trade investment — far more than who received
// it. So "settlement -> 4200" beats "Customer X -> 4300", and a rule with both
// beats either. The Wins Over column names each rule's level so this is legible
// rather than folklore.
//
// ON THE MANTLE GRID natively, same as Inter-Depot Posting and Secondary
// Posting: nothing is typed, every scope column is picked from a popover.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const seg = (on) => `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${on
  ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`;

const MUTED = { textDark: '#94a3b8' };
const MISSING = { textDark: '#dc2626' };
const SCOPED = { textDark: '#2563eb' };
const TYPED = { textDark: '#7c3aed' };

const fmtR = (n) => `R ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// MUST mirror the ORDER BY in vw_bud_revenue / vw_bud_discount_cost. If these
// two ever disagree, the page lies about which rule applies.
const specRevenue = (r) => (r.customer_code ? 4 : 0) + (r.customer_group_code ? 2 : 0) + (r.site_code ? 1 : 0);
const specDiscount = (r) => (r.discount_code ? 8 : 0) + specRevenue(r);

const revenueLevel = (r) => (r.customer_code ? 'customer'
  : r.customer_group_code ? 'customer group'
    : r.site_code ? 'site' : 'all customers');
const discountLevel = (r) => {
  const parts = [];
  if (r.discount_code) parts.push('type');
  if (r.customer_code) parts.push('customer');
  else if (r.customer_group_code) parts.push('group');
  if (r.site_code) parts.push('site');
  return parts.length ? parts.join(' + ') : 'all discounts';
};

let draftSeq = 0;

const TABS = [
  { id: 'revenue', label: 'Gross Revenue', icon: Tag },
  { id: 'discount', label: 'Discounts', icon: Percent },
];

export default function RevenuePosting() {
  const navigate = useNavigate();
  const { selectedVersion, selectedRole } = useAppContext();
  const canEdit = isAdmin(selectedRole);

  const [tab, setTab] = useState('revenue');
  const [data, setData] = useState({ revenue: [], discount: [], picks: {}, coverage: [] });
  const [drafts, setDrafts] = useState([]);          // both catalogues, tagged by kind
  const [dirty, setDirty] = useState({});            // posting_id -> patch
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [picker, setPicker] = useState(null);
  const [filterUI, setFilterUI] = useState(null);

  const isDiscountTab = tab === 'discount';
  const kind = tab;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await revenuePostingService.getPosting();
      setData(res.data || { revenue: [], discount: [], picks: {}, coverage: [] });
      setDirty({});
      setDrafts([]);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the posting catalogues.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const picks = useMemo(() => data.picks || {}, [data.picks]);
  const keyOf = (r) => r.__draft || r.posting_id;

  // ---- Discount types --------------------------------------------------------
  // Catalogue (front end) filtered by the config map (back end). Group label
  // rides along as the picker hint, so "Settlement / Prompt-Payment" is
  // findable by typing "financial" as well as by typing its own name.
  const groupOf = useMemo(() => {
    const m = {};
    DISCOUNT_GROUPS.forEach((g) => g.types.forEach((t) => { m[t.code] = g.label; }));
    return m;
  }, []);

  const enabledTypes = useMemo(() => {
    const cfg = picks.discountConfig || {};
    return ALL_DISCOUNT_TYPES.filter((t) => isEnabled(cfg, t.code));
  }, [picks.discountConfig]);

  // A type that has since been switched off on Discount Configuration must
  // still be offered if a rule already uses it — otherwise the rule becomes
  // uneditable and its cell reads as a bare code forever.
  const typeOptions = useMemo(() => {
    const inUse = new Set((data.discount || []).map((r) => r.discount_code).filter(Boolean));
    const codes = new Set(enabledTypes.map((t) => t.code));
    const extra = ALL_DISCOUNT_TYPES.filter((t) => inUse.has(t.code) && !codes.has(t.code));
    return [...enabledTypes, ...extra].map((t) => ({
      code: t.code,
      description: t.label,
      hint: `${groupOf[t.code] || ''}${codes.has(t.code) ? '' : ' · switched off'}`.trim(),
    }));
  }, [enabledTypes, data.discount, groupOf]);

  const typeLabel = useCallback((code) => {
    if (!code) return 'All types';
    const t = DISCOUNT_BY_CODE[code];
    return t ? t.label : code;
  }, []);

  // One list per catalogue, sorted BROADEST FIRST so the grid reads down in the
  // same order the LATERAL resolves: the default at the top, the exceptions
  // that beat it underneath.
  const working = useMemo(() => {
    const base = (kind === 'revenue' ? data.revenue : data.discount) || [];
    const mine = base.map((r) => ({ ...r, ...(dirty[r.posting_id] || {}) }));
    const myDrafts = drafts.filter((d) => d.__kind === kind);
    return [...mine, ...myDrafts]
      .sort((a, b) => (isDiscountTab ? specDiscount(a) - specDiscount(b) : specRevenue(a) - specRevenue(b)));
  }, [data, dirty, drafts, kind, isDiscountTab]);

  const hasChanges = Object.keys(dirty).length > 0 || drafts.length > 0;

  // ---- Label resolution ------------------------------------------------------
  const label = useCallback((list, codeKey, nameKey, code, fallback) => {
    if (!code) return fallback;
    const hit = (list || []).find((x) => x[codeKey] === code);
    return hit ? `${code} · ${hit[nameKey]}` : code;
  }, []);

  const siteLabel = useCallback((c) => label(picks.sites, 'site_code', 'site_name', c, 'All sites'), [picks.sites, label]);
  const groupLabel = useCallback((c) => label(picks.customerGroups, 'customer_group_code', 'group_name', c, '—'), [picks.customerGroups, label]);
  const custLabel = useCallback((c) => label(picks.customers, 'customer_code', 'customer_name', c, '—'), [picks.customers, label]);
  const accLabel = useCallback((c) => label(picks.accounts, 'account_code', 'account_description', c, ''), [picks.accounts, label]);
  const ccLabel = useCallback((c) => label(picks.costCenters, 'cost_center_code', 'description', c, ''), [picks.costCenters, label]);
  const scopeLabel = useCallback((r) => (r.customer_code ? custLabel(r.customer_code)
    : r.customer_group_code ? groupLabel(r.customer_group_code)
      : 'All customers'), [custLabel, groupLabel]);

  // ---- Filtering -------------------------------------------------------------
  const accessors = useMemo(() => ({
    scope: (r) => scopeLabel(r),
    site_code: (r) => siteLabel(r.site_code),
    discount_code: (r) => typeLabel(r.discount_code),
    account_code: (r) => accLabel(r.account_code),
    cost_center_code: (r) => ccLabel(r.cost_center_code),
  }), [scopeLabel, siteLabel, accLabel, ccLabel, typeLabel]);

  const { setFilter, filters, clearAll, filtered, activeIds } = useColumnFilters(working, accessors);
  const filteredRef = useRef([]);
  filteredRef.current = filtered;

  const patch = useCallback((row, p) => {
    if (row.__draft) {
      setDrafts((prev) => prev.map((d) => (d.__draft === row.__draft ? { ...d, ...p } : d)));
      return;
    }
    setDirty((prev) => ({ ...prev, [row.posting_id]: { ...(prev[row.posting_id] || {}), ...p } }));
  }, []);

  const addDraft = () => {
    draftSeq += 1;
    setDrafts((prev) => [...prev, {
      __draft: `d${draftSeq}`, __kind: kind,
      // Every scope column starts empty. A new rule is the BROADEST possible
      // one until you narrow it — which is the right default, because the
      // broadest rule is the one that stops value posting nowhere.
      site_code: null, customer_group_code: null, customer_code: null, discount_code: null,
      account_code: '', cost_center_code: '',
    }]);
  };

  // ---- Grid ------------------------------------------------------------------
  const gcols = useMemo(() => {
    const dot = (id, title) => (activeIds.includes(id) ? `${title} ●` : title);
    return [
      { id: 'scope', title: dot('scope', 'Applies To'), width: 230 },
      // Always present on the discount catalogue, never on revenue — revenue
      // has no type dimension. Reads "All types" in grey when unset, exactly
      // like Site and Applies To.
      ...(isDiscountTab ? [{ id: 'discount_code', title: dot('discount_code', 'Discount Type'), width: 190, hasMenu: true }] : []),
      { id: 'site_code', title: dot('site_code', 'Site'), width: 180, hasMenu: true },
      { id: 'account_code', title: dot('account_code', 'GL Account'), width: 260, grow: 1, hasMenu: true },
      { id: 'cost_center_code', title: dot('cost_center_code', 'Cost Centre'), width: 220, grow: 1, hasMenu: true },
      { id: 'spec', title: 'Wins Over', width: 140 },
      { id: 'del', title: '', width: 40 },
    ];
  }, [activeIds, isDiscountTab]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = filtered[row];
    if (!r) return gcell.text('', { readonly: true });
    const edited = !!r.__draft || !!dirty[r.posting_id];
    switch (id) {
      // "All customers" is the default rule and a perfectly good answer, so it
      // reads grey rather than as something unfinished.
      case 'scope':
        return gcell.text(`${edited ? '• ' : ''}${scopeLabel(r)}`,
          { readonly: true, theme: (r.customer_code || r.customer_group_code) ? SCOPED : MUTED });
      case 'discount_code':
        return gcell.text(typeLabel(r.discount_code), { readonly: true, theme: r.discount_code ? TYPED : MUTED });
      case 'site_code':
        return gcell.text(siteLabel(r.site_code), { readonly: true, theme: r.site_code ? undefined : MUTED });
      // The one genuinely broken state: no account means the value posts
      // nowhere and vanishes from the P&L without an error.
      case 'account_code':
        return gcell.text(accLabel(r.account_code) || '— posts nowhere —',
          { readonly: true, theme: r.account_code ? undefined : MISSING });
      case 'cost_center_code':
        return gcell.text(ccLabel(r.cost_center_code) || 'none', { readonly: true, theme: r.cost_center_code ? undefined : MUTED });
      case 'spec':
        return gcell.text(isDiscountTab ? discountLevel(r) : revenueLevel(r), { readonly: true, theme: MUTED });
      case 'del': return gcell.text(canEdit ? '✕' : '', { readonly: true, theme: MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, [colIds, filtered, dirty, scopeLabel, siteLabel, accLabel, ccLabel, typeLabel, isDiscountTab, canEdit]);

  const remove = useCallback(async (row) => {
    if (row.__draft) { setDrafts((prev) => prev.filter((d) => d.__draft !== row.__draft)); return; }
    setSaving(true);
    try {
      const res = await revenuePostingService.deletePosting(kind, row.posting_id);
      setToast(res.message || 'Rule removed.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally { setSaving(false); }
  }, [kind, load]);

  const onCellClicked = useCallback(([col, row], event) => {
    if (!canEdit) return;
    const r = filteredRef.current[row];
    if (!r) return;
    const id = colIds[col];
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'del') { remove(r); return; }
    if (id === 'scope') { setPicker({ rowKey: keyOf(r), field: 'scope', ...pos }); return; }
    if (['site_code', 'discount_code', 'account_code', 'cost_center_code'].includes(id)) {
      setPicker({ rowKey: keyOf(r), field: id, ...pos });
    }
  }, [colIds, canEdit, remove]);

  const onHeaderMenu = useCallback((colIndex, bounds) => {
    const colId = colIds[colIndex];
    const acc = accessors[colId];
    if (!acc) return;
    const options = [...new Set(working.map((r) => String(acc(r) ?? '')))].filter(Boolean).sort();
    setFilterUI({ colId, options, x: bounds ? bounds.x : 200, y: bounds ? bounds.y + bounds.height : 200 });
  }, [colIds, accessors, working]);

  // Scope is ONE picker over customers and groups together, not two columns.
  // A rule is scoped to a customer or to a group, never both — the API rejects
  // both — so offering two independent cells would offer an invalid rule.
  const pickerOptions = useMemo(() => {
    if (!picker) return [];
    switch (picker.field) {
      case 'scope':
        return [
          ...(picks.customerGroups || []).map((g) => ({
            code: `G:${g.customer_group_code}`, description: g.group_name, hint: 'customer group',
          })),
          ...(picks.customers || []).map((c) => ({
            code: `C:${c.customer_code}`, description: c.customer_name, hint: c.customer_group_code || 'customer',
          })),
        ];
      case 'site_code': return (picks.sites || []).map((s) => ({ code: s.site_code, description: s.site_name }));
      case 'discount_code': return typeOptions;
      case 'account_code': return (picks.accounts || []).map((a) => ({
        code: a.account_code, description: a.account_description, hint: a.account_category || '',
      }));
      case 'cost_center_code': return (picks.costCenters || []).map((c) => ({ code: c.cost_center_code, description: c.description }));
      default: return [];
    }
  }, [picker, picks, typeOptions]);

  const applyPick = (code) => {
    const row = working.find((r) => keyOf(r) === picker.rowKey);
    if (row) {
      if (picker.field === 'scope') {
        if (!code) patch(row, { customer_code: null, customer_group_code: null });
        else if (code.startsWith('G:')) patch(row, { customer_group_code: code.slice(2), customer_code: null });
        else patch(row, { customer_code: code.slice(2), customer_group_code: null });
      } else {
        patch(row, { [picker.field]: code || null });
      }
    }
    setPicker(null);
  };

  const pickerValue = useMemo(() => {
    if (!picker) return '';
    const row = working.find((r) => keyOf(r) === picker.rowKey);
    if (!row) return '';
    if (picker.field !== 'scope') return row[picker.field] || '';
    return row.customer_code ? `C:${row.customer_code}` : row.customer_group_code ? `G:${row.customer_group_code}` : '';
  }, [picker, working]);

  // ---- Save ------------------------------------------------------------------
  const save = async () => {
    const changedIds = Object.keys(dirty);
    const all = [
      ...(data.revenue || []).filter((r) => changedIds.includes(r.posting_id)).map((r) => ({ ...r, ...dirty[r.posting_id], __kind: 'revenue' })),
      ...(data.discount || []).filter((r) => changedIds.includes(r.posting_id)).map((r) => ({ ...r, ...dirty[r.posting_id], __kind: 'discount' })),
      ...drafts,
    ];
    if (!all.length) return;

    const noAccount = all.find((r) => !r.account_code);
    if (noAccount) { setError('Every rule needs a GL account, or the value it covers posts nowhere and simply disappears from the P&L.'); return; }
    const both = all.find((r) => r.customer_code && r.customer_group_code);
    if (both) { setError('A rule is scoped to a customer OR a customer group, not both — a customer already belongs to its group.'); return; }

    setSaving(true);
    setError('');
    try {
      for (const k of ['revenue', 'discount']) {
        const rules = all.filter((r) => r.__kind === k);
        if (!rules.length) continue;
        // eslint-disable-next-line no-await-in-loop
        await revenuePostingService.savePosting(k, rules.map((r) => ({
          site_code: r.site_code || null,
          customer_group_code: r.customer_group_code || null,
          customer_code: r.customer_code || null,
          discount_code: k === 'discount' ? (r.discount_code || null) : undefined,
          account_code: r.account_code,
          cost_center_code: r.cost_center_code || null,
          notes: r.notes || null,
        })));
      }
      setToast(`${all.length} rule(s) saved. Revenue and discounts now consolidate into the P&L.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save the posting rules.');
    } finally {
      setSaving(false);
    }
  };

  // The default rule — no scope at all — is the one that stops revenue posting
  // nowhere. Its absence is the single most likely reason this page is being
  // looked at, so it is called out rather than left to be deduced.
  const hasDefault = useMemo(() => {
    const list = kind === 'revenue' ? data.revenue : data.discount;
    return (list || []).some((r) => !r.site_code && !r.customer_code && !r.customer_group_code
      && (kind === 'revenue' || !r.discount_code));
  }, [data, kind]);

  const unposted = useMemo(
    () => (data.coverage || []).reduce((a, c) => a + (Number(c.unposted_amount) || 0), 0),
    [data.coverage]
  );
  const filterCount = Object.keys(filters || {}).length;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-3">
        <button className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={() => navigate('/revenue')}>
          <ArrowLeft className="w-3.5 h-3.5" /> Revenue
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Revenue Posting</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Where gross revenue and discounts land in the ledger. Map it once for <strong>all customers</strong>,
              then add exceptions by customer or customer group where the account genuinely differs.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className={btn} onClick={addDraft} disabled={saving || !canEdit}><Plus className="w-3.5 h-3.5" /> Add rule</button>
            <button className={btn} onClick={() => { setDirty({}); setDrafts([]); }} disabled={!hasChanges || saving}><Undo2 className="w-3.5 h-3.5" /> Discard</button>
            <button className={btnPrimary} onClick={save} disabled={!hasChanges || saving || !canEdit}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save{hasChanges ? ` (${Object.keys(dirty).length + drafts.length})` : ''}
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = ((t.id === 'revenue' ? data.revenue : data.discount) || []).length;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} className={seg(tab === t.id)}>
                <Icon className="w-3.5 h-3.5" />{t.label}
                <span className="ml-0.5 text-[10px] opacity-60">{count}</span>
              </button>
            );
          })}
        </div>

        {!canEdit && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              Read-only — the posting catalogue needs an administrative role. Deciding that sales is GL 4000 is a
              chart-of-accounts decision with one owner.
            </p>
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {toast && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <p className="m-0 text-xs text-emerald-700 dark:text-emerald-300">{toast}</p>
          </div>
        )}

        {!loading && !hasDefault && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
            <p className="m-0 text-xs text-red-700 dark:text-red-300">
              <strong>No default rule.</strong> Nothing here scopes to &ldquo;all customers&rdquo;, so any
              {kind === 'revenue' ? ' revenue' : ' discount'} not matched by a specific rule posts nowhere and is silently
              absent from the P&amp;L{unposted !== 0 ? ` — currently ${fmtR(unposted)}` : ''}. Add one rule with no scope
              at all and the problem is gone.
            </p>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 max-h-full flex flex-col overflow-hidden">
              {working.length === 0 ? (
                <p className="m-0 px-2 py-8 text-center text-xs text-slate-400">
                  No rules yet. Start with one that narrows nothing at all — that is &ldquo;all customers, all types&rdquo;,
                  and it is what stops the value posting nowhere.
                </p>
              ) : (
                <MantleDataGrid
                  gridId={`revenue-posting-${tab}`}
                  fitHeight
                  fitHeightPadding={6}
                  columns={gcols}
                  rowCount={filtered.length}
                  getCellContent={getCellContent}
                  onCellClicked={onCellClicked}
                  onHeaderMenuClick={onHeaderMenu}
                  freezeColumns={1}
                  hint="Click any cell to pick — nothing on this page is typed. Applies To offers customers and groups together, because a rule is one or the other. ▾ filters, Ctrl+F searches, ✕ removes. Nothing is written until you press Save."
                  toolbar={<>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      {filtered.length} of {working.length} rule{working.length === 1 ? '' : 's'}
                      {hasChanges && ' · unsaved changes'}
                    </span>
                    {filterCount > 0 && (
                      <button className="text-[11px] font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-200" onClick={clearAll}>
                        Clear filters
                      </button>
                    )}
                  </>}
                />
              )}
            </div>

            {tab === 'revenue' && (
              <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
                <strong>Most specific wins:</strong> customer beats customer group beats site beats the default.
                Gross revenue is <strong>quantity × the effective selling price</strong> — a customer-group price overrides the
                country retail price, exactly as the Selling Prices page resolves it. Revenue reaches the ledger as a
                <strong> credit</strong>, so it appears negative in the consolidated view and the P&amp;L flips it for display.
              </p>
            )}
            {isDiscountTab && (
              <>
                <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
                  Discounts are <strong>contra-revenue</strong> — a debit that reduces the credit, so they post positive.
                  A percentage discount is applied to that customer&rsquo;s or group&rsquo;s gross revenue for the period; an
                  amount discount is taken flat. Most businesses need one or two rules and nothing else.
                  Leave <strong>Discount Type</strong> as <em>All types</em> unless a particular kind of spend posts
                  somewhere different — it is the third optional scope column, not a separate thing.
                </p>
                <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
                  <strong>Type outranks customer.</strong> A discount&rsquo;s account follows what kind of spend it is —
                  settlement is a financing cost, a trade promo is marketing, a listing fee is trade investment — far more
                  often than it follows who received it. So <em>settlement → 8304</em> beats <em>Customer X → 4901</em>,
                  and a rule naming both beats either. The rows are sorted broadest-first, so the list reads down in the
                  order the rules actually resolve, and the <strong>Wins Over</strong> column names each one&rsquo;s level.
                  If that ranking is wrong for this business it is one <code className="mx-1">ORDER BY</code> in
                  <code className="mx-1">vw_bud_discount_cost</code> and the <code className="mx-1">specDiscount</code>
                  helper on this page — and the two must change together, or the column starts lying.
                </p>
                <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
                  The types on offer are the ones switched on under
                  <button className="mx-1 underline hover:text-slate-600 dark:hover:text-slate-200"
                          onClick={() => navigate('/revenue/discount-config')}>
                    Revenue &rsaquo; Discount Configuration
                  </button>
                  — {enabledTypes.length} of {ALL_DISCOUNT_TYPES.length} in the catalogue.
                  Turning one off there hides it from the picker but never breaks a rule already using it: an in-use
                  type stays offered and is marked <em>switched off</em>.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {picker && (
        <PickerPopover
          anchor={{ x: picker.x, y: picker.y }}
          value={pickerValue}
          options={pickerOptions}
          placeholder="Search…"
          emptyText="Nothing matches."
          onPick={applyPick}
          onClear={() => applyPick(null)}
          clearLabel={picker.field === 'scope' ? 'All customers'
            : picker.field === 'site_code' ? 'All sites'
              : picker.field === 'discount_code' ? 'All types' : '— none —'}
          onClose={() => setPicker(null)}
        />
      )}

      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={{ x: filterUI.x, y: filterUI.y }}
          options={filterUI.options}
          filter={filters?.[filterUI.colId]}
          onApply={(f) => { setFilter(filterUI.colId, f); setFilterUI(null); }}
          onClose={() => setFilterUI(null)}
        />
      )}
    </div>
  );
}
